import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

// ---------------------------------------------------------------------------
// Espejo del módulo `seguimiento-clientes` de la API (Plan de Retención).
//
// DOS REGLAS QUE EXPLICAN LA FORMA DE ESTOS TIPOS:
//
// 1. El semáforo llega RESUELTO. La pantalla no recalcula umbrales ni
//    prorratea la base: si lo hiciera, habría dos verdades sobre el mismo
//    cliente, y la que se discute en la reunión sería la equivocada.
// 2. No hay carga de archivos. No existe un endpoint que reciba un export, y
//    por eso no hay nada acá que se parezca a subir una planilla: el dato sale
//    de la base en vivo.
//
// Los montos vienen como `number` porque el backend los normaliza —son
// agregados de un tablero que se comparan y se pintan, no plata que se
// descuenta de una bolsa—. Las fechas de calendario vienen como
// `YYYY-MM-DD`.
// ---------------------------------------------------------------------------

export type Semaforo = 'VERDE' | 'AMARILLO' | 'ROJO' | 'SIN_BASE' | 'FUGADO';
export type SemaforoCambio = 'IGUAL' | 'RECUPERADO' | 'CAYO';
export type Tier = 'WHALE' | 'MEDIO' | 'COLA';
export type EstadoGestion =
  | 'SIN_DUENO'
  | 'EN_CONTACTO'
  | 'EN_RECUPERACION'
  | 'RECUPERADO'
  | 'PERDIDO'
  | 'NO_CONTACTABLE';
export type MotivoBaja =
  | 'TASA'
  | 'TIEMPO_RESPUESTA'
  | 'COMPETENCIA'
  | 'LIMITES'
  | 'DISPONIBILIDAD'
  | 'CERRO_NEGOCIO'
  | 'ESTACIONAL'
  | 'OTRO'
  | 'DESCONOCIDO';
export type EventoTipo =
  | 'ASIGNACION'
  | 'CONTACTO'
  | 'RESPUESTA'
  | 'TASA_OFRECIDA'
  | 'RECORDATORIO'
  | 'CAMBIO_ESTADO'
  | 'NOTA';
export type EventoCanal = 'WHATSAPP' | 'LLAMADA' | 'MAIL' | 'PRESENCIAL' | 'SISTEMA';
/** Los cuatro casos del guion de la presentación. */
export type EventoEscenario = 'TASA' | 'ME_FUI' | 'LO_PIENSO' | 'NO_CONTESTA';
export type EventoResultado =
  | 'CONTESTO'
  | 'NO_CONTESTO'
  | 'AGENDADO'
  | 'RECHAZO'
  | 'VOLVIO_A_OPERAR';

/** La cabecera de una semana. `corridaAt` es el «datos al…» de la pantalla. */
export interface Semana {
  seguimientoSemanaId: number;
  fechaInicio: string;
  fechaFin: string;
  /** Días que cubre. La semana en curso crece de a uno; la cerrada son 7. */
  dias: number;
  estado: 'ABIERTA' | 'CERRADA';
  umbralVerde: number;
  umbralAmarillo: number;
  usdtTotal: number;
  opsTotal: number;
  usdtEsperado: number;
  clientesVerde: number;
  clientesAmarillo: number;
  clientesRojo: number;
  clientesFugado: number;
  whalesRojo: number;
  fugadosNuevos: number;
  fugadosRecuperados: number;
  usdtFugadoReferencia: number;
  corridaAt: string | null;
  corridaPor: string | null;
  txConsideradas: number;
}

/** Una fila del tablero: el cliente, su semana, su semáforo y su gestión. */
export interface FilaTablero {
  clienteId: number;
  nombre: string;
  documento: string | null;
  usuarioIdPrincipal: number | null;
  ultimaOperacionAt: string | null;
  fugadoDesde: string | null;
  tier: Tier;
  usdt: number;
  ops: number;
  clp: number;
  baseUsdt: number;
  baseOps: number;
  ratioUsdt: number | null;
  ratioOps: number | null;
  /**
   * La ventana que decidió el color, desde el 15/09. `usdt`/`ops`/`baseUsdt`
   * de arriba siguen siendo los de la SEMANA —el total que se reporta no
   * cambió—; estos son los del tramo que pintó el semáforo.
   *
   * `ventanaDias = 0` es una fila vieja, pintada con la regla de la semana de
   * calendario. La pantalla tiene que poder mostrar las dos.
   */
  ventanaDias: number;
  ventanaUsdt: number;
  ventanaOps: number;
  ventanaBaseUsdt: number;
  ventanaBaseOps: number;
  ventanaRatio: number | null;
  /** Silencio: contra el p90 de huecos del propio cliente, no un plazo fijo. */
  diasSinOperar: number | null;
  silencioP90: number | null;
  silencioRel: number | null;
  silencio: boolean;
  semaforo: Semaforo;
  semaforoPrevio: Semaforo | null;
  cambio: SemaforoCambio;
  semanasEnRojo: number;
  alertaDesde: string | null;
  migracion: boolean;
  /** Cuánto valía al mes cuando estaba entero (enero–agosto 2026). */
  referenciaUsdtMensual: number | null;
  referenciaOpsMensual: number | null;
  duenoSub: string | null;
  duenoEmail: string | null;
  estadoGestion: EstadoGestion | null;
  ultimoContactoAt: string | null;
  motivoCodigo: MotivoBaja | null;
  motivoDetalle: string | null;
  proximaAccion: string | null;
  proximaAccionAt: string | null;
  tasaPreferentePct: number | null;
  tasaPreferenteHasta: string | null;
  cuentas: number;
  /** Uniones de cuentas esperando que un operador las confirme. */
  cuentasPropuestas: number;
}

export interface RespuestaSemana {
  semana: Semana;
  refrescada: boolean;
  datosAl: string | null;
  clientes: FilaTablero[];
}

/** La foto diaria de la lista de perdidos. */
export interface FotoFuga {
  fecha: string;
  fugados: number;
  nuevos: number;
  recuperados: number;
  usdtReferencia: number;
  fugadosWhale: number;
  usdtReferenciaWhale: number;
  universo: number;
}

export interface FilaReconquista {
  clienteId: number;
  nombre: string;
  tier: Tier;
  fugadoDesde: string | null;
  ultimaOperacionAt: string | null;
  diasEnLista: number | null;
  referenciaUsdtMensual: number | null;
  referenciaOpsMensual: number | null;
  duenoSub: string | null;
  duenoEmail: string | null;
  estadoGestion: EstadoGestion | null;
  ultimoContactoAt: string | null;
  motivoCodigo: MotivoBaja | null;
  proximaAccion: string | null;
  proximaAccionAt: string | null;
}

export interface Gestion {
  seguimientoClienteId: number;
  duenoSub: string | null;
  duenoEmail: string | null;
  asignadoAt: string | null;
  estadoGestion: EstadoGestion;
  ultimoContactoAt: string | null;
  ultimoContactoCanal: EventoCanal | null;
  contactosTotal: number;
  motivoCodigo: MotivoBaja | null;
  motivoDetalle: string | null;
  proximaAccion: string | null;
  proximaAccionAt: string | null;
  recordatorioAt: string | null;
  tasaPreferentePct: number | null;
  tasaPreferenteHasta: string | null;
}

export interface EventoBitacora {
  seguimientoGestionEventoId: string;
  seguimientoClienteId: number;
  seguimientoSemanaId: number | null;
  tipo: EventoTipo;
  canal: EventoCanal | null;
  escenario: EventoEscenario | null;
  resultado: EventoResultado | null;
  detalle: string | null;
  actorSub: string;
  actorEmail: string | null;
  createdAt: string;
}

/** Una semana en la historia del cliente, para la ficha. */
export interface SemanaDeCliente {
  fechaInicio: string;
  fechaFin: string;
  dias: number;
  estadoSemana: 'ABIERTA' | 'CERRADA';
  usdt: number;
  ops: number;
  baseUsdt: number;
  ratioUsdt: number | null;
  ratioOps: number | null;
  semaforo: Semaforo;
  cambio: SemaforoCambio;
  semanasEnRojo: number;
}

export interface FichaCliente {
  gestion: Gestion | null;
  semanas: SemanaDeCliente[];
  eventos: EventoBitacora[];
}

/** Los cuatro indicadores, congelados al cerrar la semana. */
export interface KpiSemana {
  seguimientoSemanaId: number;
  /** `sub` del dueño, o `GLOBAL` para el consolidado. */
  duenoSub: string;
  duenoEmail: string | null;
  alertas: number;
  alertasGestionadas: number;
  coberturaPct: number | null;
  reaccionCasos: number;
  reaccionDiasProm: number | null;
  recuperacionBase: number;
  recuperados: number;
  recuperacionPct: number | null;
  whalesRojoSinDueno: number;
  usdtRecuperado: number;
  calculadoAt: string;
}

export interface RespuestaDesempeno {
  semana: Semana | null;
  filas: KpiSemana[];
  metas: {
    coberturaPct: number;
    reaccionDias: number;
    recuperacionPct: number;
    usdtMes: number;
    recuperacionVentanaDias: number;
  };
  /**
   * El puntaje ponderado, de 0 a 100. La fase 1 pondera lo que la persona
   * controla —cobertura y reacción—; la fase 2, el resultado. El backend
   * decide en qué fase está según cuántas semanas hay cerradas.
   */
  puntajes?: Array<{ duenoSub: string; puntaje: number; fase: 1 | 2 }>;
  pesos?: Record<string, number>;
  fase?: 1 | 2;
  nota?: string;
}

/** Una unión de cuentas propuesta por nombre, esperando que alguien la resuelva. */
export interface PropuestaUnion {
  cuentaId: number;
  clienteId: number;
  usuarioId: number;
  email: string | null;
  origen: string;
  detectadaAt: string;
  titular: string;
  documentoTitular: string | null;
  nombreEnLaCuenta: string | null;
  documentoEnLaCuenta: string | null;
}

/** Lo que devuelve la siembra del universo. */
export interface ResultadoSiembra {
  cuentasEnUniverso: number;
  titularesCreados: number;
  cuentasAtadas: number;
  propuestasPorNombre: number;
  sinDocumento: number;
}

/**
 * Cómo mide el módulo. **No hay interruptor de encendido**: el seguimiento
 * corre siempre, y lo que se configura es cómo mide, no si mide.
 */
export interface ConfigSeguimiento {
  refrescoMinutos: number;
  umbralVerde: number;
  umbralAmarillo: number;
  semaforoModo: 'USDT' | 'OPS' | 'PEOR';
  /** Días corridos que mira el semáforo. 0 = la semana de calendario (regla vieja). */
  ventanaDias: number;
  /** Múltiplo del p90 de huecos propio para marcar silencio. 0 = apagado. */
  silencioFactor: number;
  tierWhaleUsdt: number;
  tierMedioUsdt: number;
  historiaDesde: string;
  historiaHasta: string;
  semanasDesde: string;
  baseMetodo: 'REFERENCIA' | 'VENTANA_MOVIL' | 'MANUAL';
  baseMeses: number;
  diasMes: number;
  fugaDias: number;
  recuperacionDias: number;
  metaCoberturaPct: number;
  metaReaccionDias: number;
  metaRecuperacionPct: number;
  metaUsdtMes: number;
  tasaPreferenteMaxPct: number;
  tasaPreferenteDias: number;
  recordatorioHoras: number;
  cadenciaMedioSemanas: number;
  telegramChatId: string;
}

/** Qué cambió en la configuración, de qué a qué. */
export interface CambioConfig {
  clave: string;
  antes: string | null;
  ahora: string;
}

export interface GuardarGestion {
  duenoSub?: string | null;
  duenoEmail?: string | null;
  estadoGestion?: EstadoGestion;
  motivoCodigo?: MotivoBaja | null;
  motivoDetalle?: string | null;
  proximaAccion?: string | null;
  proximaAccionAt?: string | null;
  tasaPreferentePct?: number | null;
  tasaPreferenteHasta?: string | null;
}

export interface RegistrarEvento {
  tipo: EventoTipo;
  canal?: EventoCanal | null;
  escenario?: EventoEscenario | null;
  resultado?: EventoResultado | null;
  detalle?: string | null;
  seguimientoSemanaId?: number | null;
  proximaAccion?: string | null;
  proximaAccionAt?: string | null;
}

@Injectable({ providedIn: 'root' })
export class SeguimientoService {
  private baseUrl = `${environment.apiNest}seguimiento`;

  constructor(private http: HttpClient) {}

  // ------------------------------------------------------------- detectar

  /**
   * El tablero en vivo. El backend refresca solo si su foto pasó la ventana de
   * refresco, así que llamar a esto seguido no dispara la agregación cada vez.
   */
  async semanaActual(): Promise<RespuestaSemana> {
    return firstValueFrom(this.http.get<RespuestaSemana>(`${this.baseUrl}/semana/actual`));
  }

  /** El botón «actualizar»: recalcula ahora, sin esperar la ventana. */
  async refrescar(): Promise<RespuestaSemana> {
    return firstValueFrom(
      this.http.post<RespuestaSemana>(`${this.baseUrl}/semana/actual/refrescar`, {}),
    );
  }

  async semanas(limite = 20): Promise<Semana[]> {
    return firstValueFrom(
      this.http.get<Semana[]>(`${this.baseUrl}/semanas`, { params: { limite } }),
    );
  }

  /** Una semana cerrada: es inmutable, así que se puede guardar en memoria. */
  async semana(id: number): Promise<RespuestaSemana> {
    return firstValueFrom(this.http.get<RespuestaSemana>(`${this.baseUrl}/semana/${id}`));
  }

  // ---------------------------------------------------------- reconquista

  async reconquista(): Promise<{ hoy: FotoFuga | null; clientes: FilaReconquista[] }> {
    return firstValueFrom(
      this.http.get<{ hoy: FotoFuga | null; clientes: FilaReconquista[] }>(
        `${this.baseUrl}/reconquista`,
      ),
    );
  }

  /** La serie diaria: lo que se mira es si la lista crece o baja. */
  async serieFuga(dias = 90): Promise<FotoFuga[]> {
    return firstValueFrom(
      this.http.get<FotoFuga[]>(`${this.baseUrl}/fuga/serie`, { params: { dias } }),
    );
  }

  // --------------------------------------------------------------- actuar

  async ficha(clienteId: number): Promise<FichaCliente> {
    return firstValueFrom(this.http.get<FichaCliente>(`${this.baseUrl}/cliente/${clienteId}`));
  }

  async guardarGestion(clienteId: number, cuerpo: GuardarGestion): Promise<Gestion> {
    return firstValueFrom(
      this.http.put<Gestion>(`${this.baseUrl}/gestion/${clienteId}`, cuerpo),
    );
  }

  /**
   * Registrar un contacto es lo que mueve el «último contacto» y el reloj del
   * tiempo de reacción. No hay forma de teclear esa fecha a mano, a propósito.
   */
  async registrarEvento(
    clienteId: number,
    cuerpo: RegistrarEvento,
  ): Promise<EventoBitacora> {
    return firstValueFrom(
      this.http.post<EventoBitacora>(`${this.baseUrl}/gestion/${clienteId}/evento`, cuerpo),
    );
  }

  /** Confirma o rechaza una unión de cuentas propuesta por nombre. */
  async resolverPropuesta(cuentaId: number, confirmar: boolean): Promise<unknown> {
    return firstValueFrom(
      this.http.post(`${this.baseUrl}/cuentas/${cuentaId}/resolver`, { confirmar }),
    );
  }

  // --------------------------------------------------------------- medir

  async desempeno(semanaId?: number, dueno?: string): Promise<RespuestaDesempeno> {
    const params: Record<string, string | number> = {};
    if (semanaId) params['semanaId'] = semanaId;
    if (dueno) params['dueno'] = dueno;
    return firstValueFrom(
      this.http.get<RespuestaDesempeno>(`${this.baseUrl}/desempeno`, { params }),
    );
  }

  /** Las uniones de cuenta que esperan una persona. */
  async propuestas(clienteId?: number): Promise<PropuestaUnion[]> {
    const params: Record<string, number> = {};
    if (clienteId) params['clienteId'] = clienteId;
    return firstValueFrom(
      this.http.get<PropuestaUnion[]>(`${this.baseUrl}/cuentas/propuestas`, { params }),
    );
  }

  /**
   * El reporte de cierre de mes. No trae margen: la ganancia real no se deriva
   * de la base, y eso viene escrito en `advertencias`.
   */
  async reporteMensual(mes?: string): Promise<any> {
    const params: Record<string, string> = {};
    if (mes) params['mes'] = mes;
    return firstValueFrom(this.http.get(`${this.baseUrl}/reporte-mensual`, { params }));
  }

  // ------------------------------------------------------------- arranque

  /**
   * Los tres pasos de puesta en marcha, en este orden. Son idempotentes: se
   * pueden correr de nuevo cuando aparecen cuentas nuevas.
   */
  async sembrarIdentidad(): Promise<ResultadoSiembra> {
    return firstValueFrom(
      this.http.post<ResultadoSiembra>(`${this.baseUrl}/sembrar/identidad`, {}),
    );
  }

  async sembrarReferencia(): Promise<any> {
    return firstValueFrom(this.http.post(`${this.baseUrl}/sembrar/referencia`, {}));
  }

  async sembrarBaseVigente(): Promise<any> {
    return firstValueFrom(this.http.post(`${this.baseUrl}/sembrar/base-vigente`, {}));
  }

  // ----------------------------------------------------------- configurar

  async config(): Promise<ConfigSeguimiento> {
    return firstValueFrom(this.http.get<ConfigSeguimiento>(`${this.baseUrl}/config`));
  }

  /**
   * Guarda el modal. Sólo hay que mandar lo que cambió, y la respuesta trae el
   * diff: mover un umbral cambia el semáforo de toda la cartera, así que la
   * pantalla muestra qué se movió y de qué a qué.
   */
  async guardarConfig(
    cambios: Record<string, string | number>,
  ): Promise<{ aplicados: CambioConfig[]; config: ConfigSeguimiento }> {
    return firstValueFrom(
      this.http.put<{ aplicados: CambioConfig[]; config: ConfigSeguimiento }>(
        `${this.baseUrl}/config`,
        cambios,
      ),
    );
  }
}
